'use client';

import { useTranslations } from 'next-intl';
import { Box, HStack } from '@terra/ui-styled-system';
import { BodyDefault, IconButton, Tooltip } from '@terra/ui-design-system';
import { Delete02Icon, File01Icon } from '@terra/ui-icons';
import type { ReportsReturnDto } from '@terra/shared-contracts-reports';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import { countPublishedRelatedFindings } from '../../../utils/get-published-related-findings';
import { ListPageTablePill } from '../../common/ListPageTablePill';
import { rowClickBlockerProps } from '../rowClickBlocker';

type ReportCellProps = {
  report: ReportsReturnDto;
};

export const getReportFindingsCount = (report: ReportsReturnDto) =>
  countPublishedRelatedFindings({ relatedFindings: report.relatedFindings });

export const getReportFilesCount = (report: ReportsReturnDto) =>
  report.files?.length ?? 0;

export function ReportTitleCell({ report }: ReportCellProps) {
  return (
    <HStack gap={2} minW={0}>
      <Box flexShrink={0} display="inline-flex" color="fg.neutral.muted">
        <File01Icon size={14} />
      </Box>
      <BodyDefault truncate>{report.title}</BodyDefault>
    </HStack>
  );
}

export function ReportSourceCell({ report }: ReportCellProps) {
  const t = useTranslations('proxy.reports.source');

  return <ListPageTablePill>{t(report.source)}</ListPageTablePill>;
}

function ReportCount({ count }: { count: number }) {
  return (
    <BodyDefault
      color="fg.neutral.muted"
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {count.toLocaleString()}
    </BodyDefault>
  );
}

export function ReportFindingsCell({ report }: ReportCellProps) {
  return <ReportCount count={getReportFindingsCount(report)} />;
}

export function ReportFilesCell({ report }: ReportCellProps) {
  return <ReportCount count={getReportFilesCount(report)} />;
}

export function ReportCreatedAtCell({ report }: ReportCellProps) {
  const dateFormatter = useDateFormatter();

  return (
    <BodyDefault color="fg.neutral.muted" truncate>
      {dateFormatter.format(new Date(report.createdAt))}
    </BodyDefault>
  );
}

export function ReportUpdatedAtCell({ report }: ReportCellProps) {
  const dateFormatter = useDateFormatter();

  return (
    <BodyDefault color="fg.neutral.muted" truncate>
      {dateFormatter.format(new Date(report.updatedAt))}
    </BodyDefault>
  );
}

export function ReportActionsCell({
  report,
  onDelete,
}: ReportCellProps & { onDelete: (report: ReportsReturnDto) => void }) {
  const t = useTranslations('proxy.reports.table');

  return (
    <Box display="inline-flex" {...rowClickBlockerProps}>
      <Tooltip title={t('deleteReport')}>
        <IconButton
          size="xs"
          variant="ghost"
          aria-label={t('deleteReport')}
          onClick={() => onDelete(report)}
        >
          <Delete02Icon size={14} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
